import { getBookingStepLink } from '@fcom/common/utils/booking-common.utils';
import { isBlank, safeMap } from '@fcom/core/utils/utils';
import { MILLIS_IN_HOUR, MILLIS_IN_MINUTE } from '@fcom/core/utils/date';
import { pad } from '@fcom/core/utils/format';
import { CmsOffer, CmsTarget, CmsTaxonomy } from '@fcom/core-api';
import { AspectRatios } from '@fcom/ui-components';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';

import { BlockOfferData } from '../../interfaces/destination-promo.interface';
import { Destination } from '../../interfaces/destination.interface';

export const offsetToTimeZone = (offsetMillis: number): string => {
  const negative = offsetMillis < 0;
  const offset = Math.abs(offsetMillis);
  const hours = Math.floor(offset / MILLIS_IN_HOUR);
  const remainder = offset % MILLIS_IN_HOUR;
  const minutes = Math.floor(remainder / MILLIS_IN_MINUTE);
  if (hours === 0 && minutes === 0) {
    return 'UTC';
  }
  const timeStr = `UTC${negative ? '-' : '+'}${hours}`;
  if (minutes > 0) {
    return `${timeStr}:${pad(minutes)}`;
  }
  return timeStr;
};

/**
 * CMS destination (nestingDepth = 1) to destination
 */
export const toDestination: (obj: unknown) => Destination = ({
  currency,
  defaultLocation: {
    locationCode,
    geoLocation: { countryCode },
  },
  flightDuration,
  localSettings: { temperature, rainfall },
  numberOfFlights,
  picture,
  timeZoneOffset,
  title,
  htmlTitle,
}) => ({
  avgRainfall: rainfall,
  avgTemperature: temperature,
  currencyCode: currency,
  flightDuration,
  imageData: picture,
  locationCode,
  countryCode,
  numberOfFlights,
  timeZoneOffset,
  timeZone: offsetToTimeZone(timeZoneOffset),
  title,
  htmlTitle,
});

export const toOfferLink = (obj: { booking?: string; url?: string }, lang: string): string => {
  if (obj.booking) {
    return getBookingStepLink(lang, BOOKING_STEPS.ENTRY, { destination: obj.booking });
  }
  return obj.url;
};

export const toPromoBlock = (data: CmsOffer, lang?: string): BlockOfferData => {
  const link = data?.target?.url ? data.target.url : toOfferLink(data, lang);
  const title = data.title || data?.target?.title;
  // TODO: Make CMS return empty teaserTitle as CTA if left empty, not copy of the title
  const callToAction = data.teaserTitle === data.title ? null : data.teaserTitle;

  return {
    title: title,
    price: data.price,
    subtitle: data.teaserText,
    callToAction: callToAction,
    link: link,
    flag: data.flag,
    imageData: data.picture,
    destination: data.defaultLocationCode ?? resolveLocationCode(data),
  };

  function resolveLocationCode(cmsOffer: CmsOffer): string {
    if (isBlank(cmsOffer.booking)) {
      return safeMap(cmsOffer.target, (target) => target.locationCode);
    }
    return cmsOffer.booking;
  }
};

export const toLinkData = (a: {
  teaserTitle?: string;
  title?: string;
  external?: boolean;
  url?: string;
  openInNewTab?: boolean;
}): CmsTarget | null =>
  !a
    ? null
    : {
        title: a.teaserTitle || a.title,
        external: !!a.external,
        url: a.url || '',
        openInNewTab: !!a.openInNewTab,
      };

export const findAspectRatio = (tags: CmsTaxonomy[]): keyof typeof AspectRatios | undefined =>
  tags
    ? (Object.keys(AspectRatios).find(
        (aspectRatio) => tags.indexOf(('cmstyle-image-aspect-ratio-' + aspectRatio) as CmsTaxonomy) >= 0
      ) as keyof typeof AspectRatios)
    : undefined;
